/**
 * SSRF 防护
 * 校验 URL 是否为公网可访问地址
 */

const BLOCKED_HOSTS = new Set([
    'localhost',
    'localhost.localdomain',
    'ip6-localhost',
    'ip6-loopback',
    'broadcasthost',
]);

/**
 * 判断是否为私有 IPv4
 */
function isPrivateIPv4(ip: string): boolean {
    const parts = ip.split('.');

    if (parts.length !== 4) {
        return false;
    }

    const nums = parts.map(Number);

    if (
        nums.some(n =>
            !Number.isInteger(n) ||
            n < 0 ||
            n > 255,
        )
    ) {
        return false;
    }

    const [a, b] = nums;

    return (
        a === 10 ||

        // 127.0.0.0/8
        a === 127 ||

        // 0.0.0.0/8
        a === 0 ||

        // 169.254.0.0/16
        (a === 169 && b === 254) ||

        // 172.16.0.0 ~ 172.31.255.255
        (a === 172 && b >= 16 && b <= 31) ||

        // 192.168.0.0/16
        (a === 192 && b === 168) ||

        // carrier-grade NAT
        (a === 100 && b >= 64 && b <= 127) ||

        // multicast / reserved
        a >= 224
    );
}

/**
 * 判断是否为私有 IPv6
 */
function isPrivateIPv6(ip: string): boolean {
    const normalized = ip
        .replace(/^\[|\]$/g, '')
        .toLowerCase();

    // loopback / unspecified
    if (
        normalized === '::1' ||
        normalized === '::'
    ) {
        return true;
    }

    // link local
    if (normalized.startsWith('fe80:')) {
        return true;
    }

    // unique local address
    if (
        normalized.startsWith('fc') ||
        normalized.startsWith('fd')
    ) {
        return true;
    }

    // IPv4-mapped IPv6
    if (normalized.startsWith('::ffff:')) {
        const v4 = normalized.slice(7);

        if (
            /^\d{1,3}(\.\d{1,3}){3}$/.test(v4)
        ) {
            return isPrivateIPv4(v4);
        }
    }

    return false;
}

/**
 * 是否为 IP 地址
 */
function isPrivateOrReservedIp(host: string): boolean {
    return (
        isPrivateIPv4(host) ||
        isPrivateIPv6(host)
    );
}

/**
 * 校验 URL
 */
export function assertSafePublicUrl(url: string): void {
    let parsed: URL;

    try {
        parsed = new URL(url);
    } catch {
        throw new Error('Invalid URL');
    }

    // 仅允许 http/https
    if (
        !['http:', 'https:'].includes(
            parsed.protocol,
        )
    ) {
        throw new Error(
            'Only http/https protocols are allowed',
        );
    }

    // 禁止携带认证信息
    if (
        parsed.username ||
        parsed.password
    ) {
        throw new Error(
            'URL credentials are not allowed',
        );
    }

    // URL 长度限制
    if (url.length > 2000) {
        throw new Error('URL is too long');
    }

    const hostname =
        parsed.hostname.toLowerCase();

    if (!hostname) {
        throw new Error('Invalid hostname');
    }

    // localhost
    if (BLOCKED_HOSTS.has(hostname)) {
        throw new Error(
            'Access to local hosts is not allowed',
        );
    }

    // .local 域名
    if (hostname.endsWith('.local')) {
        throw new Error(
            'Local network domains are not allowed',
        );
    }

    // metadata
    if (
        hostname === '169.254.169.254'
    ) {
        throw new Error(
            'Cloud metadata address is not allowed',
        );
    }

    // IP 校验
    if (isPrivateOrReservedIp(hostname)) {
        throw new Error(
            'Private or reserved IP addresses are not allowed',
        );
    }
}